import React, { FC, useEffect, useRef, useState } from 'react';
import { AutocompleteRenderInputParams, Avatar, Box, Chip, Divider, MenuItem, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import {
	DefaultFieldTypeStatusArray,
	DistrictDto,
	EngagementStatusArray,
	LanguageEnum,
	ProviderDto,
} from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { AutocompleteTextField, CoreAutoComplete } from '../../../_core/components/_ui/core-autocomplete';
import { buttonModalWindowStyles, chipDeleteIconStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { getObjectLanguage } from '../../../_core/components/languages-component';
import { CoreCheckbox } from '../../../_core/components/_ui/core-checkbox';
import { ChipCloseIcon, CleanFiltersIcon } from '../../../_core/components/icons';
import { CoreOverflowTypographyTooltip } from '../../../_core/components/_ui/core-overflow-tooltip';
import CoreCalendar from '../../../_core/components/_ui/core-date-picker/core-calendar/core-calendar.component';
import { useGetDistrictListQuery } from '../../../../store/api/district.api';
import { useGetOptionsFieldsByConditionQuery } from '../../../../store/api/customRequirements.api';
import { useGetAllProvidersQuery } from '../../../../store/api/providers.api';
import { useGetEngagementStatusesQuery, useGetStatusesLinguistLinkQuery } from '../../../../store/api/engagements.api';

export type DashboardFiltersModalProps = CoreModalProps & {
	isAdminRoute: boolean;
	currentDistrict?: DistrictDto | null;
	defaultValues?: any;
	submitAction?: any;
	closeModal(): void;
};

export const DashboardFiltersModal: FC<DashboardFiltersModalProps> = (props) => {
	const [t] = useTranslation();
	const { submitAction, defaultValues, closeModal, isAdminRoute, currentDistrict, ...modalProps } = props;
	const { data: districtList } = useGetDistrictListQuery();

	const methods = useForm({
		defaultValues,
		mode: 'all',
	});

	const {
		register,
		handleSubmit,
		reset,
		control,
		watch,
		getValues,
		formState: { isLoading, isSubmitting },
	} = methods;

	const loadingSubmit = isLoading || isSubmitting;
	const currentDuration = watch('duration');
	const currentDistricts: DistrictDto[] = watch('districts');
	const currentLanguages = watch('languages');
	const currentProviders = watch('providers');
	//const currentLinkType = watch('linkType');

	const isLinguistLink = !!currentDistrict?.districtLinguistLinkId;

	//const isLinguistLink = currentLinkType === 'linguist-link';

	const { data: statusesList } = useGetEngagementStatusesQuery();
	const { data: statusesLinguistlinkList } = useGetStatusesLinguistLinkQuery();

	const { data: schooOptionsList } = useGetOptionsFieldsByConditionQuery({
		name: ['School'],
		districtId: currentDistricts?.map((district: DistrictDto) => district._id),
	});

	const { data: serviceOptionsList } = useGetOptionsFieldsByConditionQuery({
		name: ['Service'],
		districtId: currentDistricts?.map((district: DistrictDto) => district._id),
	});

	const { data: providersList } = useGetAllProvidersQuery({
		languages: currentLanguages?.length ? currentLanguages.join(',') : '',
		districts: currentDistricts?.map((district: DistrictDto) => district._id).join(','),
	});

	const { data: programOptionsList } = useGetOptionsFieldsByConditionQuery({
		name: ['Program'],
		districtId: currentDistricts?.map((district: DistrictDto) => district._id),
	});

	const [filteredLanguages, setFilteredLanguages] = useState(Object.values(LanguageEnum));

	useEffect(() => {
		if (currentProviders?.length) {
			setFilteredLanguages(
				Object.values(LanguageEnum)?.filter((language) =>
					currentProviders.some((provider: ProviderDto) => provider.targetLanguage.includes(language))
				)
			);
		}
	}, [currentProviders]);

	//const prevLinkType = useRef(currentLinkType);

	/*useEffect(() => {
		if (!currentLinkType) return;

		if (prevLinkType.current !== currentLinkType) {
			reset({
				districts: currentDistrict ? [currentDistrict] : [],
				engagementType: [],
				duration: [null, null],
				schools: [],
				programs: [],
				services: [],
				providers: [],
				languages: [],
				engagementStatus: [],
				//linkType: currentLinkType,
			});
			prevLinkType.current = currentLinkType;
		}
	}, [currentLinkType, reset, currentDistrict]);*/

	const handleResetForm = async () => {
		reset({
			districts: currentDistrict ? [currentDistrict] : [],
			engagementType: [],
			duration: [null, null],
			schools: [],
			programs: [],
			services: [],
			providers: [],
			languages: [],
			engagementStatus: [],
			//linkType: 'family-link',
		});
		submitAction(getValues());
	};

	const handleFormSubmit = async (values: any) => {
		submitAction(values);
		closeModal();
	};

	const customOnClose = () => {
		submitAction(getValues());
		closeModal();
	};

	return (
		<CoreModal
			customOnClose={customOnClose}
			{...modalProps}
			modalWidth={373}
			bodySx={{ pt: 0, pb: '16px', px: '16px' }}
			paperSx={{ width: '100%', borderRadius: 0, height: '100vh', maxHeight: '100vh', margin: 0 }}
			containerSx={{ pt: 2 }}
			dialogSx={{
				'& .MuiDialog-container': {
					justifyContent: 'flex-end',
				},
			}}
			hideCloseIcon
			useSlideTransion
			moveSideSlideTransition={'left'}
			disableEscapeKeyDown
		>
			<Box
				sx={{
					mb: 2,
					display: 'flex',
					alignItems: 'center',
					position: 'sticky',
					top: 0,
					zIndex: 1,
					backgroundColor: (theme) => theme.colors.white,
				}}
			>
				<Typography variant="h5" sx={{ fontSize: '16px', fontStyle: 'normal', fontWeight: 700 }}>
					{'Filters'}
				</Typography>
				<CoreButton
					variant="secondary"
					onClick={handleResetForm}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						px: 1,
						py: '4px',
						ml: 'auto',
						border: 'none',
						':hover': { backgroundColor: '#F1F1F1', border: 'none', color: (theme) => theme.colors.black },
						':active': { backgroundColor: '#E8E8E8', border: 'none', color: (theme) => theme.colors.black },
					}}
				>
					<CleanFiltersIcon />
					<Box component="span" sx={{ color: (theme) => theme.colors.black, fontSize: '12px' }}>
						Reset filters
					</Box>
				</CoreButton>
			</Box>
			<Divider sx={{ mb: 2 }} />
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
					<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
						{/*<CoreAutoComplete
							{...register('linkType')}
							control={control}
							disabled={isAdminRoute}
							multiple={false}
							isFilter
							disableClearable
							fullWidth
							customLabelSx={{
								color: (theme) => theme.colors.grayLightBlue,
								fontSize: '12px',
								fontWeight: 500,
							}}
							label={'Data Source'}
							options={['family-link', 'linguist-link']}
							getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
							isOptionEqualToValue={(opt, val) => opt.value === val.value}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('engagementType')?.length ? t('common.filter-engagement-type') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
									}}
								/>
							)}
						/>*/}

						<Box sx={{ width: '100%' }}>
							<Controller
								name={'duration'}
								control={control}
								render={({ field: { value, ...fieldWithoutValue } }) => (
									<CoreCalendar
										{...fieldWithoutValue}
										selectsRange
										inline={false}
										startDate={currentDuration?.length ? currentDuration[0] : null}
										endDate={currentDuration?.length ? currentDuration[1] : null}
										onChange={(dates: [Date | null, Date | null]) => {
											fieldWithoutValue.onChange(dates);
										}}
										onKeyDown={(e) => {
											e.preventDefault();
										}}
										onClear={() => {
											fieldWithoutValue.onChange([null, null]);
										}}
										inputProps={{
											label: 'Date',
											fullWidth: true,
											customLabelSx: {
												color: (theme) => theme.colors.grayLightBlue,
												fontSize: '12px',
												fontWeight: 500,
											},
										}}
									/>
								)}
							/>
						</Box>
						<CoreAutoComplete
							multiple={true}
							{...register('engagementType')}
							control={control}
							isFilter
							fullWidth
							customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
							label={'Engagement Type'}
							options={DefaultFieldTypeStatusArray}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option === value}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('engagementType')?.length ? t('common.filter-engagement-type') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => (
								<MenuItem
									{...props}
									key={option}
									sx={{
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<CoreCheckbox checked={selected} sx={{ mr: 1, p: 0, ml: 0 }} />
									<CoreOverflowTypographyTooltip>{option}</CoreOverflowTypographyTooltip>
								</MenuItem>
							)}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={value[0]}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
						{isAdminRoute ? (
							<CoreAutoComplete
								multiple={true}
								{...register('districts')}
								control={control}
								isFilter
								fullWidth
								customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
								label={'District'}
								options={districtList ?? []}
								getOptionLabel={(option) => option.name || ''}
								selectOnFocus={true}
								isOptionEqualToValue={(option, value) => option._id === value._id}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValues('districts')?.length ? t('common.filter-district') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
											'& .MuiInputBase-input': {
												height: '100%',
												padding: '0',
											},
											'& .MuiOutlinedInput-root': {
												'&.Mui-focused fieldset': {
													borderColor: (theme) => theme.colors.blue,
												},
											},
										}}
									/>
								)}
								renderOption={(props, option, { selected }) => (
									<MenuItem
										{...props}
										key={option._id}
										sx={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />
										<CoreOverflowTypographyTooltip>{option.name}</CoreOverflowTypographyTooltip>
									</MenuItem>
								)}
								renderTags={(value, getTagProps) => {
									const count = value.length - 1;

									return [
										value[0] && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: 0 })}
												label={value[0]?.name}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
										count > 0 && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: count })}
												label={`+${count}`}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
									];
								}}
							/>
						) : null}

						<CoreAutoComplete
							multiple={true}
							{...register('schools')}
							control={control}
							disabled={!currentDistricts?.length || isLinguistLink}
							isFilter
							fullWidth
							customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
							label={'School'}
							options={schooOptionsList ?? []}
							getOptionLabel={(option) => option.text || ''}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option._id === value._id}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('schools')?.length ? t('common.filter-school') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
										'& .MuiOutlinedInput-root': {
											'&.Mui-focused fieldset': {
												borderColor: (theme) => theme.colors.blue,
											},
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => (
								<MenuItem
									{...props}
									key={option._id}
									sx={{
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />
									<CoreOverflowTypographyTooltip>{option.text}</CoreOverflowTypographyTooltip>
								</MenuItem>
							)}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={value[0]?.text}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
						<CoreAutoComplete
							multiple={true}
							{...register('services')}
							control={control}
							disabled={!currentDistricts?.length || isLinguistLink}
							isFilter
							fullWidth
							customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
							label={'Service'}
							options={serviceOptionsList ?? []}
							getOptionLabel={(option) => option.text || ''}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option._id === value._id}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('services')?.length ? t('common.filter-service') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
										'& .MuiOutlinedInput-root': {
											'&.Mui-focused fieldset': {
												borderColor: (theme) => theme.colors.blue,
											},
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => (
								<MenuItem
									{...props}
									key={option._id}
									sx={{
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />
									<CoreOverflowTypographyTooltip>{option.text}</CoreOverflowTypographyTooltip>
								</MenuItem>
							)}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={value[0]?.text}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
						<CoreAutoComplete
							multiple={true}
							{...register('providers')}
							control={control}
							disabled={!currentDistricts?.length || isLinguistLink}
							isFilter
							fullWidth
							customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
							label={'Provider'}
							options={providersList ?? []}
							getOptionLabel={(option) => option.fullName || ''}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option._id === value._id}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('providers')?.length ? t('common.filter-provider') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
										'& .MuiOutlinedInput-root': {
											'&.Mui-focused fieldset': {
												borderColor: (theme) => theme.colors.blue,
											},
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => (
								<MenuItem
									{...props}
									key={option._id}
									sx={{
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />

									<Avatar
										src={option.imageUrl}
										alt={option.fullName}
										style={{ width: 24, height: 24, marginRight: 18, borderRadius: '6px' }}
									/>
									<CoreOverflowTypographyTooltip>{option.fullName}</CoreOverflowTypographyTooltip>
								</MenuItem>
							)}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={value[0]?.fullName}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
						<CoreAutoComplete
							multiple={true}
							{...register('languages')}
							control={control}
							isFilter
							fullWidth
							customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
							label={'Language'}
							options={filteredLanguages}
							getOptionLabel={(option) => {
								const language = getObjectLanguage(option);

								return language ? language.name : '';
							}}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option === value}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('languages')?.length ? t('common.filter-lang') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => {
								const language = getObjectLanguage(option);

								return (
									<MenuItem
										{...props}
										key={language?.value}
										sx={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<CoreCheckbox checked={selected} sx={{ mr: 1, p: 0, ml: 0 }} />
										<CoreOverflowTypographyTooltip>{language?.name}</CoreOverflowTypographyTooltip>
									</MenuItem>
								);
							}}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={getObjectLanguage(value[0])?.name}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
						<CoreAutoComplete
							multiple={true}
							{...register('programs')}
							control={control}
							disabled={!currentDistricts?.length || isLinguistLink}
							isFilter
							fullWidth
							customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
							label={'Program'}
							options={programOptionsList ?? []}
							getOptionLabel={(option) => option.text || ''}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option._id === value._id}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('programs')?.length ? t('common.filter-program') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
										'& .MuiOutlinedInput-root': {
											'&.Mui-focused fieldset': {
												borderColor: (theme) => theme.colors.blue,
											},
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => (
								<MenuItem
									{...props}
									key={option._id}
									sx={{
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<CoreCheckbox checked={selected} sx={{ mr: 2, p: 0, ml: 0 }} />
									<CoreOverflowTypographyTooltip>{option.text}</CoreOverflowTypographyTooltip>
								</MenuItem>
							)}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={value[0]?.text}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
						<CoreAutoComplete
							multiple={true}
							{...register('engagementStatus')}
							control={control}
							isFilter
							fullWidth
							customLabelSx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '12px', fontWeight: 500 }}
							label={'Engagement Status'}
							options={(isLinguistLink ? statusesLinguistlinkList : statusesList) ?? []}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option._id === value._id}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValues('engagementStatus')?.length ? t('common.filter-engagement-status') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => (
								<MenuItem
									{...props}
									key={option._id}
									value={option._id}
									sx={{
										display: 'flex',
										alignItems: 'center',
									}}
								>
									<CoreCheckbox checked={selected} sx={{ mr: 1, p: 0, ml: 0 }} />
									<CoreOverflowTypographyTooltip>{option.statusName}</CoreOverflowTypographyTooltip>
								</MenuItem>
							)}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={value[0].statusName}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
					</Box>
					<Box
						sx={{
							width: '100%',
							mt: 3,
							position: 'sticky',
							bottom: 0,
							zIndex: 1,
							backgroundColor: (theme) => theme.colors.white,
						}}
					>
						<CoreButton
							type="submit"
							contraModeStyleButton
							loading={loadingSubmit}
							sx={mergeSx(buttonModalWindowStyles, { width: '100%' })}
						>
							{'Apply changes'}
						</CoreButton>
					</Box>
				</Box>
			</LocalizationProvider>
		</CoreModal>
	);
};
